import { Link, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  CalendarDays,
  Info,
  Loader2,
  Mail,
  MousePointerClick,
} from 'lucide-react';
import { BucketBadge } from '@/components/BucketBadge';
import { ScoreBreakdown } from '@/components/ScoreBreakdown';
import { useApiQuery, type MemberDetailResponse } from '@/lib/api';

const fmtDate = (iso: string) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—';

const TENURE_LABELS: Record<string, string> = {
  Year1: 'First year',
  Years2to5: '2–5 years',
  Years6Plus: '6+ years',
};

export function MemberDetail() {
  const { profileId } = useParams<{ profileId: string }>();
  const { data, loading, error } = useApiQuery<MemberDetailResponse>(
    profileId ? `/api/members/${profileId}` : null,
  );

  if (loading) {
    return (
      <div className="grid min-h-[60vh] place-items-center text-muted-foreground">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="container mx-auto max-w-5xl px-6 py-10">
        <BackLink />
        <p className="mt-6 rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error ?? 'Member not found.'}
        </p>
      </div>
    );
  }

  const { member, score, history } = data;
  const { emailTotals } = history;
  const tenure = score?.components?.tenureBucket ?? member.tenureBucket;

  return (
    <div className="container mx-auto max-w-6xl px-6 py-10">
      <BackLink />

      <header className="mt-4 flex flex-wrap items-start justify-between gap-6">
        <div className="min-w-0">
          <h1 className="text-3xl font-bold tracking-tight">{member.legalName ?? member.profileId}</h1>
          <p className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
            {member.city && (
              <span>
                {member.city}
                {member.state ? `, ${member.state}` : ''}
              </span>
            )}
            {tenure && (
              <span title={score?.components?.joinedAtInferred ? 'Inferred from the earliest registration on record — MC Trade holds no join date' : undefined}>
                · {TENURE_LABELS[tenure] ?? tenure}
                {score?.components?.joinedAtInferred && '*'}
              </span>
            )}
            {member.status && <span>· {member.status}</span>}
            <span className="font-mono text-xs">· #{member.profileId}</span>
          </p>
          {member.email && (
            <a
              href={`mailto:${member.email}`}
              className="mt-2 inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
            >
              <Mail className="h-3.5 w-3.5" />
              {member.email}
            </a>
          )}
        </div>

        {score && (
          <div className="shrink-0 rounded-xl border border-border bg-card px-6 py-4 text-center shadow-sm">
            <p className="text-4xl font-bold tabular-nums">{score.score}</p>
            <p className="text-xs uppercase tracking-widest text-muted-foreground">engagement</p>
            <BucketBadge bucket={score.bucket} className="mt-3" />
          </div>
        )}
      </header>

      {score && <Flags score={score} />}

      <div className="mt-8 grid gap-6 lg:grid-cols-5">
        <section className="lg:col-span-3 rounded-xl border border-border bg-card p-6 shadow-sm">
          <h2 className="text-lg font-semibold">Why this score</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Each signal scored 0–100, then weighted. The weighted points add up to {score?.score ?? 0}.
          </p>
          {score ? (
            <ScoreBreakdown rows={score.breakdown} className="mt-6" />
          ) : (
            <p className="mt-6 text-sm text-muted-foreground">
              This member has not been scored yet.
            </p>
          )}
          {score && (
            <p className="mt-6 border-t border-border pt-4 text-xs text-muted-foreground">
              Last scored {new Date(score.computedAt).toLocaleString()}
            </p>
          )}
        </section>

        <section className="lg:col-span-2 space-y-6">
          <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
            <h2 className="flex items-center gap-2 text-lg font-semibold">
              <Mail className="h-4 w-4 text-primary" />
              Email engagement
            </h2>
            {history.campaignCount === 0 ? (
              <p className="mt-3 text-sm text-muted-foreground">
                No Constant Contact activity matched to this member.
              </p>
            ) : (
              <>
                <div className="mt-4 grid grid-cols-3 gap-3 text-center">
                  <Stat label="opens" value={emailTotals.opens} />
                  <Stat label="clicks" value={emailTotals.clicks} />
                  <Stat label="campaigns" value={history.campaignCount} />
                </div>
                <p className="mt-3 text-xs text-muted-foreground">
                  Engaged with {emailTotals.campaignsEngaged} of {history.campaignCount} campaigns sent.
                  {emailTotals.proxyOpens > 0 &&
                    ` ${emailTotals.proxyOpens} machine opens excluded.`}
                </p>
                <ul className="mt-4 divide-y divide-border text-sm">
                  {history.campaigns.slice(0, 6).map((c, i) => (
                    <li key={i} className="flex items-start justify-between gap-3 py-2.5">
                      <div className="min-w-0">
                        <p className="truncate font-medium">{c.campaignName}</p>
                        <p className="text-xs text-muted-foreground">
                          {fmtDate(c.sentDate)}
                          {c.matchType === 'domain' && ' · matched by domain'}
                        </p>
                      </div>
                      <span className="shrink-0 whitespace-nowrap text-xs tabular-nums text-muted-foreground">
                        {c.opens}
                        <Mail className="mx-1 inline h-3 w-3" />
                        {c.clicks > 0 && (
                          <>
                            {c.clicks}
                            <MousePointerClick className="ml-1 inline h-3 w-3 text-primary" />
                          </>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>

          <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
            <h2 className="flex items-center gap-2 text-lg font-semibold">
              <CalendarDays className="h-4 w-4 text-primary" />
              Registrations
            </h2>
            {history.registrationCount === 0 ? (
              <p className="mt-3 text-sm text-muted-foreground">No registrations on record.</p>
            ) : (
              <>
                <p className="mt-1 text-sm text-muted-foreground">
                  {history.registrationCount} on record, most recent first.
                </p>
                <ul className="mt-4 divide-y divide-border text-sm">
                  {history.registrations.slice(0, 8).map((r, i) => (
                    <li key={i} className="flex items-start justify-between gap-3 py-2.5">
                      <div className="min-w-0">
                        <p className="truncate font-medium">{r.eventName}</p>
                        <p className="text-xs text-muted-foreground">{fmtDate(r.registeredAt)}</p>
                      </div>
                      {r.amount !== undefined && (
                        <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                          ${r.amount.toFixed(2)}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
                <p className="mt-3 text-xs text-muted-foreground">
                  Drawn from invoice line items, so payment fees appear here but are excluded from scoring.
                </p>
              </>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

function BackLink() {
  return (
    <Link
      to="/members"
      className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
    >
      <ArrowLeft className="h-4 w-4" />
      All members
    </Link>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg bg-muted/50 py-3">
      <p className="text-xl font-bold tabular-nums">{value.toLocaleString()}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
  );
}

/** Caveats the score alone does not convey. */
function Flags({ score }: { score: NonNullable<MemberDetailResponse['score']> }) {
  const notes: Array<{ tone: 'warn' | 'info'; text: string }> = [];

  if (score.inactivityFlagApplied) {
    notes.push({
      tone: 'warn',
      text: 'No event activity for over two years — capped at At Risk so they always surface.',
    });
  }
  if (score.cohortFloorApplied) {
    notes.push({
      tone: 'info',
      text: 'First-year member with no registrations yet — held at Watching rather than written off.',
    });
  }
  if (score.components?.recencyDays === null) {
    notes.push({
      tone: 'info',
      text: 'No usable activity date on record. Recency was set aside rather than scored as zero, and its weight spread across the other signals.',
    });
  }
  if (score.touchpointConfidence === 'low') {
    notes.push({
      tone: 'info',
      text: 'No logged calls or meetings. Contact logging is unreliable, so this lowers confidence rather than the score.',
    });
  }

  if (notes.length === 0) return null;

  return (
    <ul className="mt-6 space-y-2">
      {notes.map((n, i) => (
        <li
          key={i}
          className={
            n.tone === 'warn'
              ? 'flex gap-2.5 rounded-lg bg-bucket-atrisk/10 px-4 py-3 text-sm text-bucket-atrisk'
              : 'flex gap-2.5 rounded-lg bg-muted/60 px-4 py-3 text-sm text-muted-foreground'
          }
        >
          {n.tone === 'warn' ? (
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          ) : (
            <Info className="mt-0.5 h-4 w-4 shrink-0" />
          )}
          <span>{n.text}</span>
        </li>
      ))}
    </ul>
  );
}
